// src/pages/KnowledgeBase.js
import React, { useState, useMemo, useEffect, useContext } from "react";
import { useAnalytics } from "../hooks/useAnalytics";
import { Link } from "react-router-dom";
import "./KnowledgeBase.css";
import { MODE, MARKETING_LABEL } from "../config/compliance";
import Seo from "../components/Seo";
import { LangContext } from "../lang/LangProvider";

const FALLBACK_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 400'>
    <defs>
      <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='#eef2ff'/>
        <stop offset='100%' stop-color='#dbe4ff'/>
      </linearGradient>
    </defs>
    <rect width='640' height='400' fill='url(#g)'/>
    <g fill='none' stroke='#99a7d6' stroke-width='6'>
      <rect x='80' y='90' width='240' height='180' rx='16'/>
      <polyline points='116,228 170,176 214,210 256,160 300,196'/>
    </g>
  </svg>`
);
const DATA_URI_FALLBACK = `data:image/svg+xml;utf8,${FALLBACK_SVG}`;

const KnowledgeBase = () => {
  const { lang, t, articles } = useContext(LangContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("all");
  const { trackKBFilter, trackKBSearch } = useAnalytics();

  // Traccia filtro tag
  useEffect(() => {
    if (selectedTag !== "all") trackKBFilter(selectedTag, lang);
  }, [selectedTag]); // eslint-disable-line react-hooks/exhaustive-deps

  // Traccia ricerca (con debounce 600ms — il count viene calcolato inline)
  useEffect(() => {
    if (!searchTerm.trim()) return;
    const timer = setTimeout(() => {
      const count = articles.filter((a) => {
        const s = searchTerm.toLowerCase();
        return a.title.toLowerCase().includes(s) || a.metaDescription.toLowerCase().includes(s);
      }).length;
      trackKBSearch(searchTerm, lang, count);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const allTags = useMemo(() => {
    const tagSet = new Set();
    articles.forEach((a) => a.tags?.forEach((t) => tagSet.add(t)));
    return ["all", ...Array.from(tagSet)];
  }, [articles]);

  const filteredArticles = useMemo(
    () =>
      articles.filter((a) => {
        const s = searchTerm.toLowerCase();
        const byText =
          a.title.toLowerCase().includes(s) ||
          a.metaDescription.toLowerCase().includes(s);
        const byTag = selectedTag === "all" || a.tags?.includes(selectedTag);
        return byText && byTag;
      }),
    [articles, searchTerm, selectedTag]
  );

  // Se la chiave i18n manca, t() ritorna la chiave stessa ("kb.tags.x"):
  // in quel caso ripieghiamo sul tag grezzo invece di mostrare la chiave.
  const mapTag = (tag) => {
    const key = `kb.tags.${tag}`;
    const label = t(key);
    return label === key ? tag : label;
  };

  return (
    <div className="kb-bg" aria-label={MARKETING_LABEL}>
      <Seo
        title="Knowledge Base"
        description={
          lang === "en"
            ? "Articles and guides on ETFs, asset allocation and risk management with a quantitative and AI approach."
            : "Articoli e guide su ETF, asset allocation e gestione del rischio con approccio quantitativo e AI."
        }
        path={`/${lang}/kb`}
        image="/og/cover-kb.png"
      />

      <section className="kb-hero">
        <h1>{t("kb.title")}</h1>
        <p>
          <strong>
            {MODE === "PRE"
              ? lang === "en"
                ? "Educational"
                : "Contenuti educativi"
              : lang === "en"
              ? "In-depth"
              : "di approfondimento"}
          </strong>{" "}
          {lang === "en"
            ? "on markets and quantitative methods."
            : "su mercati e metodi quantitativi."}
        </p>
      </section>

      <section className="kb-search-section">
        <input
          type="text"
          className="kb-search-input"
          placeholder={t("kb.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="kb-filters">
          {allTags.map((tKey) => (
            <button
              key={tKey}
              className={`kb-filter-tag ${selectedTag === tKey ? "active" : ""}`}
              onClick={() => setSelectedTag(tKey)}
            >
              {mapTag(tKey)}
            </button>
          ))}
        </div>
      </section>

      <section className="kb-articles-grid">
        {filteredArticles.length ? (
          filteredArticles.map((a) => {
            // 1) scegliamo la thumbnail: listImage -> coverImage -> default brand -> svg
            const desired = a.listImage || a.coverImage || DATA_URI_FALLBACK;// "/assets/kb/kb-default.png";
            return (
              <article key={a.slug || a.title} className="kb-article-card">
                <Link
                  to={`/${lang}/kb/${a.slug || encodeURIComponent(a.title)}`}
                  className="kb-article-link kb-article-row"
                >
                  {/* THUMB */}
                  {desired ? (
                    <img
                      className="kb-article-thumb"
                      src={desired}
                      alt={a.title}
                      loading="lazy"
                      onError={(e) => {
                        if (e.currentTarget.src !== DATA_URI_FALLBACK) {
                          e.currentTarget.src = DATA_URI_FALLBACK;
                        }
                      }}
                    />
                  ) : (
                    <div className="kb-article-thumb kb-article-thumb--ph" aria-hidden="true" />
                  )}

                  {/* BODY */}
                  <div className="kb-article-body">
                    <h3>{a.title}</h3>
                    <div className="article-meta">
                      {(a.readTime || (lang === "en" ? "5 min" : "5 min"))} {t("kb.readTime")}
                    </div>
                    <p>{a.metaDescription}</p>
                    {a.tags && (
                      <div className="kb-article-tags">
                        {a.tags.map((tg) => (
                          <span key={tg} className="kb-tag">
                            {mapTag(tg)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              </article>
            );
          })
        ) : (
          <div className="kb-empty-state">
            <h3>{t("kb.emptyTitle")}</h3>
            <p>{t("kb.emptyDesc")}</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default KnowledgeBase;
